import type { SidebarGroupType, SidebarNavigationType, SidebarType } from '@/types/sidebar';

const sidebarLabelMap: Record<string, string> = {
  Overview: 'Tổng quan',
  Dashboard: 'Dashboard',
  Customers: 'Khách hàng',
  Companies: 'Công ty',
  'Orders & Billing': 'Đơn hàng & thanh toán',
  Orders: 'Đơn hàng',
  Billing: 'Thanh toán',
  Invoices: 'Hóa đơn',
  Coupon: 'Mã giảm giá',
  Coupons: 'Mã giảm giá',
  'Payment Settings': 'Cổng thanh toán',
  Catalog: 'Catalog',
  'Service Catalog': 'Danh mục dịch vụ',
  'Form Fields': 'Form fields',
  System: 'Hệ thống',
  'Audit Log': 'Nhật ký hoạt động',
  Notifications: 'Thông báo',
  'Main Menu': 'Menu chính',
};

export const translateSidebarLabel = (label?: string | null) => {
  if (!label) return '';

  return sidebarLabelMap[label] || label;
};

const normalizeSidebarRoute = (item: SidebarType) => {
  if (item.key === 'admin-form-fields' || item.label === 'Form Fields') {
    return '/admin/form-fields';
  }

  return item.route;
};

const translateSidebarItem = (item: SidebarType): SidebarType => ({
  ...item,
  label: translateSidebarLabel(item.label),
  route: normalizeSidebarRoute(item),
  children: item.children?.map(translateSidebarItem),
});

export const isSidebarGroup = (item?: SidebarNavigationType | null): item is SidebarGroupType => {
  return Array.isArray((item as SidebarGroupType | undefined)?.menus);
};

export const getSidebarOrder = (item?: { order?: number; sortOrder?: number } | null) => {
  return item?.order ?? item?.sortOrder ?? 0;
};

export const sortSidebarItems = <T extends { order?: number; sortOrder?: number }>(items?: T[]) => {
  return [...(items || [])].sort((a, b) => getSidebarOrder(a) - getSidebarOrder(b));
};

export const flattenSidebarItems = (items?: SidebarNavigationType[]): SidebarType[] => {
  return sortSidebarItems(items).flatMap((item) => {
    if (isSidebarGroup(item)) {
      return sortSidebarItems(item.menus);
    }

    return item ? [item] : [];
  });
};

export const getSidebarGroups = (items?: SidebarNavigationType[]) => {
  const safeItems = sortSidebarItems(items);

  if (safeItems.some(isSidebarGroup)) {
    return safeItems.map((item) => {
      if (isSidebarGroup(item)) {
        return {
          label: translateSidebarLabel(item.label),
          items: sortSidebarItems(item.menus).map(translateSidebarItem),
        };
      }

      return {
        label: translateSidebarLabel('Main Menu'),
        items: [translateSidebarItem(item)],
      };
    });
  }

  return [
    {
      label: translateSidebarLabel('Main Menu'),
      items: sortSidebarItems(safeItems as SidebarType[]).map(translateSidebarItem),
    },
  ];
};

export const findFirstSidebarRoute = (items?: SidebarNavigationType[]) => {
  const visit = (entries?: SidebarType[]): string | null => {
    for (const item of sortSidebarItems(entries)) {
      if (item.route) return item.route;

      const childRoute = visit(item.children);
      if (childRoute) return childRoute;
    }

    return null;
  };

  for (const item of sortSidebarItems(items)) {
    if (isSidebarGroup(item)) {
      const groupRoute = visit(item.menus);
      if (groupRoute) return groupRoute;
      continue;
    }

    if (item?.route) return item.route;

    const childRoute = visit(item?.children);
    if (childRoute) return childRoute;
  }

  return null;
};
